import { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Pencil,
  Image as ImageIcon,
  Eye,
  Heart,
  ThumbsUp,
  Coins,
  Film,
  Calendar,
  Tag as TagIcon,
  Building2,
  Users,
  ShoppingBag,
  PlayCircle,
} from "lucide-react";
import {
  getDramaWithEpisodes,
  getDramaCoverUrl,
  getDramaFavoritesCount,
  getDramaTotalLikes,
} from "@/services/firebase/drama";
import { getTags } from "@/services/firebase/tag";
import { getActors, getActorPhotoUrl } from "@/services/firebase/actor";
import { artistAdminList, artistHubEnabled } from "@/services/firebase/artistHub";
import { castPositionLabel, castStatusChips } from "@/lib/cast";
import { getCompanies } from "@/services/firebase/company";
import { getProducts } from "@/services/firebase/product";
import { getDramaEpisodeStats } from "@/services/firebase/transaction";
import {
  getAdUnlockStats,
  currentPeriodTaipei,
} from "@/services/firebase/ad_unlock_stats";
import { VideoMeta } from "@/types/video";
import { useDisplayAdjustments } from "@/hooks/useDisplayAdjustments";
import { dramaAdjustment } from "@/services/firebase/displayMetric";
import {
  AdminPage,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminDataTable,
  AdminStatusBadge,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import VideoPreviewDialog from "@/components/video/VideoPreviewDialog";
import { Input } from "@/components/ui/input";
import { Download } from "lucide-react";

const episodeColumns: AdminTableColumn[] = [
  { key: "order", label: "#", width: "w-12" },
  { key: "caption", label: "標題" },
  { key: "duration", label: "時長", hideBelow: "md" },
  { key: "play_times", label: "播放數", hideBelow: "md" },
  { key: "vip", label: "付費" },
  { key: "status", label: "狀態" },
  { key: "preview", label: "預覽", width: "w-20" },
];

function formatDuration(seconds?: number): string {
  if (!seconds) return "-";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function DramaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // M4: 集數預覽 dialog
  const [previewVideo, setPreviewVideo] = useState<
    (VideoMeta & { id: string }) | null
  >(null);
  // 廣告解鎖 stats 月份選擇（預設當月）
  const [adStatsPeriod, setAdStatsPeriod] = useState<string>(currentPeriodTaipei());

  // 主要劇集資料(含 episodes)
  const {
    data: dramaData,
    isLoading: loadingDrama,
    error: dramaError,
  } = useQuery({
    queryKey: ["drama-with-episodes", id],
    queryFn: () => getDramaWithEpisodes(id!),
    enabled: !!id,
  });

  const drama = dramaData?.drama;
  const videos = dramaData?.videos ?? [];

  const sortedVideos = useMemo(
    () => [...videos].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [videos]
  );
  const currentFreeCount = useMemo(() => {
    let count = 0;
    for (const v of sortedVideos) {
      if (v.vip === false) count += 1;
      else break;
    }
    return count;
  }, [sortedVideos]);

  // 統計資料(平行載入)
  const { data: favoritesCount } = useQuery({
    queryKey: ["drama-favorites-count", id],
    queryFn: () => getDramaFavoritesCount(id!),
    enabled: !!id,
  });
  const { data: totalLikes } = useQuery({
    queryKey: ["drama-total-likes", id],
    queryFn: () => getDramaTotalLikes(id!),
    enabled: !!id,
  });
  // 後台統計一律顯示真實值：欄位值 − App 顯示數據調整量
  const { data: displayAdjustments } = useDisplayAdjustments();
  const likeAdjustmentTotal = Object.values(displayAdjustments?.get(id ?? "")?.videos ?? {}).reduce(
    (sum, v) => sum + Number(v.likeCount || 0),
    0
  );
  const { data: episodeStats } = useQuery({
    queryKey: ["drama-episode-stats", id],
    queryFn: () => getDramaEpisodeStats(id!),
    enabled: !!id,
  });

  // 關聯實體(用於把 ID 轉成名稱)
  const { data: tags } = useQuery({ queryKey: ["tags"], queryFn: () => getTags() });
  const { data: actors } = useQuery({ queryKey: ["actors"], queryFn: () => getActors() });
  const { data: companies } = useQuery({ queryKey: ["companies"], queryFn: () => getCompanies() });
  const { data: products } = useQuery({ queryKey: ["products"], queryFn: () => getProducts() });

  // ID → 物件 map
  const tagMap = useMemo(() => {
    const m = new Map<string, { tw?: string; en?: string; tag_id?: string }>();
    tags?.forEach((t) => m.set(t.id, t));
    return m;
  }, [tags]);

  const actorMap = useMemo(() => {
    const m = new Map<string, { name: string; photoURL?: string }>();
    actors?.forEach((a) => m.set(a.id, a));
    return m;
  }, [actors]);

  // 卡司藝人的專頁 / 認證狀態（只在 artistHubEnabled 環境）
  const castIds = drama?.actors ?? [];
  const { data: castStatus } = useQuery({
    queryKey: ["artist-admin", "ids", castIds],
    queryFn: () => artistAdminList(castIds.slice(0, 200)),
    enabled: artistHubEnabled && castIds.length > 0,
    staleTime: 60 * 1000,
    retry: false,
  });
  const castStatusById = useMemo(
    () => new Map((castStatus?.items ?? []).map((r) => [r.artistId, r])),
    [castStatus]
  );

  const company = useMemo(() => {
    if (!drama?.company || !companies) return null;
    return companies.find((c) => c.id === drama.company) ?? null;
  }, [drama?.company, companies]);

  const productMap = useMemo(() => {
    const m = new Map<string, { name: string }>();
    products?.forEach((p) => m.set(p.id, p));
    return m;
  }, [products]);

  // 總解鎖收益(金幣)
  const totalUnlockRevenue = episodeStats?.totalUnlockRevenue ?? 0;

  // 廣告解鎖統計（僅在 drama 開啟廣告解鎖時查詢）
  const adUnlockEnabled = drama?.ad_unlock_enabled === true;
  const { data: adUnlockStats, isLoading: loadingAdStats } = useQuery({
    queryKey: ["ad-unlock-stats", id, adStatsPeriod],
    queryFn: () => getAdUnlockStats(id!, adStatsPeriod),
    enabled: !!id && adUnlockEnabled,
  });

  // episode_number → caption 對照（用於 stats 表格 join）
  const episodeCaptionMap = useMemo(() => {
    const m = new Map<number, string>();
    sortedVideos.forEach((v) => {
      if (v.order != null) m.set(v.order, v.caption || v.name || "");
    });
    return m;
  }, [sortedVideos]);

  if (loadingDrama) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }

  if (dramaError || !drama) {
    return (
      <AdminPage>
        <AdminErrorAlert
          message={`載入劇集時發生錯誤:${
            dramaError instanceof Error ? dramaError.message : "找不到劇集"
          }`}
        />
        <Button variant="outline" onClick={() => navigate("/dramas")} className="mt-4">
          <ArrowLeft className="h-4 w-4" />
          返回列表
        </Button>
      </AdminPage>
    );
  }

  const coverUrl = getDramaCoverUrl(drama.drama_cover_url);
  const isVertical = drama.drama_video_orientation !== 1; // 舊資料缺欄位視為直式（與 App/DramaCard 的 === 1 判橫式一致）

  return (
    <AdminPage>
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <Button variant="outline" size="icon" onClick={() => navigate("/dramas")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold text-foreground truncate">
                {drama.drama_title || "(未命名)"}
              </h1>
              {drama.isReleased ? (
                <Badge variant="success">已上架</Badge>
              ) : (
                <Badge variant="secondary">未上架</Badge>
              )}
              {drama.new_release && (
                <Badge variant="info">新上架</Badge>
              )}
            </div>
            {drama.drama_title_en && (
              <p className="mt-0.5 text-sm text-muted-foreground">{drama.drama_title_en}</p>
            )}
            <p className="mt-0.5 text-xs font-mono text-muted-foreground">{drama.drama_id}</p>
          </div>
        </div>
        <Button asChild>
          <Link to={`/dramas/${id}/edit`}>
            <Pencil className="h-4 w-4" />
            編輯
          </Link>
        </Button>
      </div>

      {/* Hero card: cover + 統計 */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Cover */}
            <div
              className={`relative shrink-0 overflow-hidden rounded-xl border bg-muted ${
                "w-48 aspect-[2/3]" /* 封面一律 2:3 直式，橫式劇亦同 */
              }`}
            >
              {coverUrl ? (
                <img
                  src={coverUrl}
                  alt={drama.drama_title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-16 h-16 text-muted-foreground" />
                </div>
              )}
            </div>

            {/* 統計 */}
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-4">
              <StatCard
                icon={Film}
                label="集數"
                value={(drama.drama_length || videos.length || 0).toLocaleString()}
              />
              <StatCard
                icon={Eye}
                label="總觀看"
                value={(
                  (drama.drama_play_times || 0) -
                  dramaAdjustment(displayAdjustments, id, "drama_play_times")
                ).toLocaleString()}
              />
              <StatCard
                icon={Heart}
                label="總收藏"
                value={(
                  (favoritesCount ?? drama.favorites_count ?? 0) -
                  dramaAdjustment(displayAdjustments, id, "favorites_count")
                ).toLocaleString()}
              />
              <StatCard
                icon={ThumbsUp}
                label="總喜愛"
                value={((totalLikes ?? 0) - likeAdjustmentTotal).toLocaleString()}
              />
              <StatCard
                icon={Coins}
                label="總解鎖收益"
                value={`${totalUnlockRevenue.toLocaleString()} 金幣`}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 基本資訊 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>基本資訊</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <Field label="影片方向" value={isVertical ? "直式" : "橫式"} />
            <Field label="Drama ID" value={<span className="font-mono">{drama.drama_id}</span>} />
            <Field
              label={
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> 建立時間
                </span>
              }
              value={
                drama.created_at ? new Date(drama.created_at).toLocaleString("zh-TW") : "-"
              }
            />
            <Field
              label={
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> 最後更新
                </span>
              }
              value={
                drama.updated_at ? new Date(drama.updated_at).toLocaleString("zh-TW") : "-"
              }
            />
            <div className="sm:col-span-2">
              <div className="text-xs text-muted-foreground mb-1">搜尋關鍵字</div>
              {drama.search_keywords && drama.search_keywords.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {drama.search_keywords.map((kw, i) => (
                    <Badge key={i} variant="secondary" className="font-normal">
                      {kw}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 關聯 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>關聯</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {/* 標籤 */}
          <div>
            <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
              <TagIcon className="h-3 w-3" /> 標籤
            </div>
            {drama.tags && drama.tags.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {drama.tags.map((tagId) => {
                  const tag = tagMap.get(tagId);
                  return (
                    <Badge key={tagId} variant="outline" className="font-normal">
                      {tag?.tw || tag?.en || tag?.tag_id || tagId}
                    </Badge>
                  );
                })}
              </div>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </div>

          {/* 製作公司 */}
          <div>
            <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
              <Building2 className="h-3 w-3" /> 製作公司
            </div>
            {company ? (
              <Link
                to={`/companies/${company.id}`}
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                {company.company_name || company.id}
              </Link>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </div>

          {/* 演員 */}
          <div>
            <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
              <Users className="h-3 w-3" /> 演員
            </div>
            {drama.actors && drama.actors.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {drama.actors.map((actorId, index) => {
                  const actor = actorMap.get(actorId);
                  const chips = artistHubEnabled ? castStatusChips(castStatusById.get(actorId)) : [];
                  const photoUrl = actor ? getActorPhotoUrl(actor.photoURL) : "";
                  return (
                    <Link
                      key={actorId}
                      to={`/actors/${actorId}/edit`}
                      aria-label={`${castPositionLabel(index)}：${actor?.name ?? actorId}（前往藝人頁）`}
                      className="flex items-center gap-2 rounded-full border bg-card pr-3 pl-1 py-1 hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={actor?.name ?? actorId}
                          width={28}
                          height={28}
                          loading="lazy"
                          decoding="async"
                          className="w-7 h-7 rounded-full object-cover bg-muted"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                          <Users className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                      <span className="text-xs text-muted-foreground">{castPositionLabel(index)}</span>
                      <span className={actor ? "text-sm" : "font-mono text-xs"}>{actor?.name ?? actorId}</span>
                      {chips.map((c) => (
                        <AdminStatusBadge key={c.label} label={c.label} tone={c.tone} />
                      ))}
                    </Link>
                  );
                })}
              </div>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </div>

          {/* 相關商品 */}
          <div>
            <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
              <ShoppingBag className="h-3 w-3" /> 相關商品
            </div>
            {drama.related_products && drama.related_products.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {drama.related_products.map((pid) => {
                  const p = productMap.get(pid);
                  return (
                    <Badge key={pid} variant="outline" className="font-normal">
                      {p?.name || pid}
                    </Badge>
                  );
                })}
              </div>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 定價設定 */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle>定價設定</CardTitle>
          <Button asChild variant="outline" size="sm">
            <Link to={`/dramas/${id}/pricing`}>修改定價</Link>
          </Button>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {/* 單集解鎖成本 */}
          <Field
            label="單集解鎖成本"
            value={
              drama.episode_unlock_coin_cost != null && drama.episode_unlock_coin_cost > 0 ? (
                `${drama.episode_unlock_coin_cost} 金幣`
              ) : (
                <span className="text-muted-foreground">預設 30 金幣</span>
              )
            }
          />

          {/* 全劇一次買斷 */}
          <div className="pt-3 border-t">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-muted-foreground">全劇一次買斷（金幣）</span>
              {drama.enable_full_drama_coin_purchase ? (
                <Badge variant="success">已啟用</Badge>
              ) : (
                <Badge variant="secondary">未啟用</Badge>
              )}
            </div>
            {drama.enable_full_drama_coin_purchase && (
              <div className="ml-1 mt-2">
                <Field
                  label="所需金幣"
                  value={`${(drama.full_drama_coin_cost || 0).toLocaleString()} 金幣`}
                />
              </div>
            )}
          </div>

          {/* 免費集數 */}
          <div className="pt-3 border-t">
            <Field
              label="免費集數"
              value={
                videos.length > 0
                  ? `前 ${currentFreeCount} 集`
                  : <span className="text-muted-foreground">尚無集數</span>
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* 廣告解鎖數據（僅在啟用時顯示） */}
      {adUnlockEnabled && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle>廣告解鎖數據</CardTitle>
              <Badge variant="success" className="text-[10px]">本劇已啟用</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="month"
                value={adStatsPeriod}
                onChange={(e) => setAdStatsPeriod(e.target.value)}
                className="w-40"
              />
              <Button
                variant="outline"
                size="sm"
                disabled={!adUnlockStats || adUnlockStats.episodes.length === 0}
                onClick={() => {
                  if (!adUnlockStats) return;
                  const rows = [
                    ["集數", "標題", "解鎖次數", "最近解鎖時間"],
                    ...adUnlockStats.episodes.map((e) => [
                      String(e.episode_number),
                      episodeCaptionMap.get(e.episode_number) || "",
                      String(e.unlocked_count),
                      e.last_unlocked_at
                        ? new Date(e.last_unlocked_at).toLocaleString("zh-TW")
                        : "",
                    ]),
                  ];
                  const csv = rows
                    .map((r) =>
                      r
                        .map((c) => `"${String(c).replace(/"/g, '""')}"`)
                        .join(",")
                    )
                    .join("\n");
                  const blob = new Blob(["﻿" + csv], {
                    type: "text/csv;charset=utf-8;",
                  });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `ad_unlock_${drama.drama_id || id}_${adStatsPeriod}.csv`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                }}
              >
                <Download className="h-4 w-4" />
                CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* 月總計 */}
            <div className="mb-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
              <StatCard
                icon={Coins}
                label={`${adStatsPeriod} 月總解鎖次數`}
                value={
                  loadingAdStats
                    ? "..."
                    : (adUnlockStats?.total_unlocked_count ?? 0).toLocaleString()
                }
              />
              <StatCard
                icon={Film}
                label="有解鎖的集數"
                value={
                  loadingAdStats ? "..." : (adUnlockStats?.episodes.length ?? 0).toLocaleString()
                }
              />
            </div>

            {/* episode 表格 */}
            {loadingAdStats ? (
              <div className="text-sm text-muted-foreground py-6 text-center">載入中…</div>
            ) : !adUnlockStats || adUnlockStats.episodes.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">
                {adStatsPeriod} 沒有任何廣告解鎖紀錄
              </div>
            ) : (
              <AdminDataTable
                columns={[
                  { key: "episode_number", label: "集數", width: "w-16" },
                  { key: "caption", label: "標題" },
                  { key: "unlocked_count", label: "解鎖次數" },
                  { key: "last_unlocked_at", label: "最近解鎖時間", hideBelow: "md" },
                ]}
                data={adUnlockStats.episodes}
                getRowId={(e) => String(e.episode_number)}
                renderCell={(row, col) => {
                  switch (col.key) {
                    case "episode_number":
                      return (
                        <span className="text-sm font-mono tabular-nums">
                          {row.episode_number}
                        </span>
                      );
                    case "caption":
                      return (
                        <span className="text-sm">
                          {episodeCaptionMap.get(row.episode_number) || "-"}
                        </span>
                      );
                    case "unlocked_count":
                      return (
                        <span className="text-sm tabular-nums font-semibold">
                          {row.unlocked_count.toLocaleString()}
                        </span>
                      );
                    case "last_unlocked_at":
                      return (
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {row.last_unlocked_at
                            ? new Date(row.last_unlocked_at).toLocaleString("zh-TW")
                            : "-"}
                        </span>
                      );
                    default:
                      return null;
                  }
                }}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* 集數列表 */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle>集數列表 {videos.length > 0 ? `(${videos.length})` : ""}</CardTitle>
            {videos.length > 0 && currentFreeCount > 0 && (
              <Badge variant="success" className="text-[10px]">
                前 {currentFreeCount} 集免費
              </Badge>
            )}
          </div>
          {/* 集數編輯統一走右上角「編輯」按鈕，這裡不另開入口 */}
        </CardHeader>
        <CardContent>
          <AdminDataTable
            columns={episodeColumns}
            data={videos}
            getRowId={(item) => item.id || ""}
            emptyTitle="此劇集尚無集數"
            renderCell={(video: VideoMeta & { id: string }, col) => {
              switch (col.key) {
                case "order":
                  return (
                    <span className="text-sm font-mono tabular-nums">{video.order ?? "-"}</span>
                  );
                case "caption":
                  return (
                    <span className="text-sm">{video.caption || video.name || "-"}</span>
                  );
                case "duration":
                  return (
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {formatDuration(video.duration)}
                    </span>
                  );
                case "play_times":
                  return (
                    <span className="text-sm tabular-nums">
                      {(video.play_times || 0).toLocaleString()}
                    </span>
                  );
                case "vip": {
                  if (!video.vip) {
                    return <Badge variant="success">免費</Badge>;
                  }
                  // 生效單集價：集級覆寫 → 劇層 → 預設(30)
                  const hasOverride =
                    typeof video.episode_unlock_coin_cost === "number" &&
                    video.episode_unlock_coin_cost > 0;
                  const dramaCost =
                    typeof drama.episode_unlock_coin_cost === "number" &&
                    drama.episode_unlock_coin_cost > 0
                      ? drama.episode_unlock_coin_cost
                      : 30;
                  const effective = hasOverride
                    ? (video.episode_unlock_coin_cost as number)
                    : dramaCost;
                  return (
                    <div className="flex items-center gap-1.5">
                      <Badge className="bg-amber-500 text-white hover:bg-amber-600">
                        🔒 {effective} 金幣
                      </Badge>
                      {hasOverride && (
                        <Badge variant="secondary" title="本集為集級覆寫價，與全劇預設不同">
                          覆寫
                        </Badge>
                      )}
                    </div>
                  );
                }
                case "status":
                  if (video.release_status === "released" || video.is_released) {
                    return (
                      <Badge variant="success">已上架</Badge>
                    );
                  }
                  if (video.release_status === "scheduled") {
                    return (
                      <Badge variant="info">已排程</Badge>
                    );
                  }
                  return <Badge variant="secondary">未上架</Badge>;
                case "preview":
                  return (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewVideo(video);
                      }}
                      title="預覽集數資訊"
                    >
                      <PlayCircle className="h-4 w-4" />
                      <span className="ml-1 text-xs">預覽</span>
                    </Button>
                  );
                default:
                  return null;
              }
            }}
          />
        </CardContent>
      </Card>

      {/* M4: 集數預覽 Dialog */}
      <VideoPreviewDialog
        isOpen={previewVideo !== null}
        onClose={() => setPreviewVideo(null)}
        video={previewVideo}
        dramaId={drama.drama_id || id || ""}
        dramaTitle={drama.drama_title}
      />
    </AdminPage>
  );
}

// ── 小元件 ───────────────────────────────────

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Field({
  label,
  value,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div>{value}</div>
    </div>
  );
}
